/**
 * Safe wrappers around localStorage and sessionStorage. Safari private mode, blocked site data and
 * storage quotas can all make these throw, so every access is wrapped and failures fall back to an
 * in-memory map for the life of the page.
 *
 * Never store quiz answers here (the brief forbids it). Tokens, order drafts and UI preferences are fine.
 */

export interface SafeStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  getJSON<T>(key: string, guard: (v: unknown) => v is T): T | null;
  setJSON(key: string, value: unknown): void;
  /** Removes every key that starts with one of `prefixes`. */
  removePrefixed(prefixes: readonly string[]): void;
}

function make(pick: () => Storage | null): SafeStorage {
  const memory = new Map<string, string>();
  const backend = (): Storage | null => {
    try {
      return pick();
    } catch {
      return null;
    }
  };
  const api: SafeStorage = {
    get(key) {
      try {
        const b = backend();
        if (b) return b.getItem(key);
      } catch {
        /* fall through to memory */
      }
      return memory.get(key) ?? null;
    },
    set(key, value) {
      memory.set(key, value);
      try {
        backend()?.setItem(key, value);
      } catch {
        /* quota or private mode: memory copy is kept */
      }
    },
    remove(key) {
      memory.delete(key);
      try {
        backend()?.removeItem(key);
      } catch {
        /* ignore */
      }
    },
    getJSON<T>(key: string, guard: (v: unknown) => v is T): T | null {
      const raw = api.get(key);
      if (raw === null) return null;
      try {
        const v: unknown = JSON.parse(raw);
        return guard(v) ? v : null;
      } catch {
        return null;
      }
    },
    setJSON(key, value) {
      try {
        api.set(key, JSON.stringify(value));
      } catch {
        /* unserializable: ignore */
      }
    },
    removePrefixed(prefixes) {
      const hit = (k: string) => prefixes.some((p) => k.startsWith(p));
      for (const k of [...memory.keys()]) if (hit(k)) memory.delete(k);
      try {
        const b = backend();
        if (!b) return;
        const keys: string[] = [];
        for (let i = 0; i < b.length; i++) {
          const k = b.key(i);
          if (k !== null && hit(k)) keys.push(k);
        }
        for (const k of keys) b.removeItem(k);
      } catch {
        /* ignore */
      }
    },
  };
  return api;
}

export const local = make(() => (typeof window !== 'undefined' ? window.localStorage : null));
export const session = make(() => (typeof window !== 'undefined' ? window.sessionStorage : null));

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
